import { useState, useCallback, useEffect, useRef, type MouseEvent } from 'react';

const CONFIRM_WIDTH = 260;
const VIEWPORT_PADDING = 16;

interface ICloseConfirmationReturn {
  confirmPosition: { top: number, left: number } | null,
  confirmRef: React.RefObject<HTMLDivElement | null>,
  requestClose: (e: MouseEvent) => void,
  confirmClose: () => void,
  cancelClose: () => void,
}

const useCloseConfirmation = (
  onClose: () => void,
  confirmOnClose: boolean,
): ICloseConfirmationReturn => {
  const [confirmPosition, setConfirmPosition] = useState<{ top: number, left: number } | null>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  const requestClose = useCallback((e: MouseEvent): void => {
    if (!confirmOnClose) {
      onClose();
      return;
    }

    const maxLeft = window.innerWidth - CONFIRM_WIDTH - VIEWPORT_PADDING;
    setConfirmPosition({
      top: Math.max(e.clientY, VIEWPORT_PADDING),
      left: Math.min(e.clientX, maxLeft),
    });
  }, [confirmOnClose, onClose]);

  const confirmClose = useCallback((): void => {
    setConfirmPosition(null);
    onClose();
  }, [onClose]);

  const cancelClose = useCallback((): void => {
    setConfirmPosition(null);
  }, []);

  useEffect(() => {
    if (!confirmPosition) return undefined;

    const handleClickOutside = (e: globalThis.MouseEvent): void => {
      if (confirmRef.current && !confirmRef.current.contains(e.target as Node)) {
        setConfirmPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [confirmPosition]);

  return { confirmPosition, confirmRef, requestClose, confirmClose, cancelClose };
};

export { useCloseConfirmation };
